/**
 * ClientOnboardingWelcome Component
 * Initial welcome page for client-facing onboarding process
 * Displays when client clicks the secure onboarding link
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import publicTokenService from '../../services/publicTokenService';
import { OnboardingProgressBar } from '../../components/ui';
import styles from './ClientOnboardingWelcome.module.css';

const ClientOnboardingWelcome = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [error, setError] = useState(null);
    const [tokenData, setTokenData] = useState(null);
    const [isTestMode, setIsTestMode] = useState(false);
    const [savedProgress, setSavedProgress] = useState(null);

    // Helper function to get route from step number
    const getRouteForStep = (stepNumber) => {
        const routes = {
            1: 'terms',
            2: 'account-type',
            3: 'business-info',
            4: 'billing-reporting',
            5: 'reporting-requirements',
            6: 'qc-disposal',
            7: 'review'
        };
        return routes[stepNumber] || 'terms';
    };

    // Validate token and check for saved progress on mount
    useEffect(() => {
        const validateToken = async () => {
            try {
                setLoading(true);
                
                // Check if in test mode
                const testMode = sessionStorage.getItem('onboarding_test_mode') === 'true';
                setIsTestMode(testMode);
                
                if (testMode) {
                    // Mock successful validation for test mode
                    setTokenValid(true);
                    setTokenData({
                        valid: true,
                        expiresAt: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(), // 3 days from now
                        purpose: 'client_onboarding',
                        message: 'Test Mode - This is a preview'
                    });
                    setLoading(false);
                    return;
                }
                
                const response = await publicTokenService.validateToken(token);
                
                if (response.valid) {
                    setTokenValid(true);
                    setTokenData(response);
                    
                    // Check for saved progress - first from validation response, then from getProgress
                    let progressData = null;
                    
                    // Check if currentStep is in the validation response
                    if (response.currentStep && response.currentStep > 1) {
                        progressData = {
                            currentStep: response.currentStep,
                            totalSteps: response.totalSteps,
                            completedSteps: response.completedSteps || []
                        };
                    } else {
                        // Try to fetch from getProgress endpoint
                        try {
                            const progress = await publicTokenService.getProgress(token);
                            
                            // Handle nested response structure (progress.progress)
                            const actualProgress = progress.progress || progress;
                            
                            // Use lastSavedStep if available, otherwise currentStep
                            const stepToResume = actualProgress.lastSavedStep || actualProgress.currentStep;
                            
                            if (actualProgress && stepToResume && stepToResume > 1) {
                                progressData = {
                                    ...actualProgress,
                                    currentStep: stepToResume
                                };
                            }
                        } catch (progressErr) {
                            // No saved progress available
                        }
                    }
                    
                    // If we have progress data, auto-redirect
                    if (progressData && progressData.currentStep > 1) {
                        setSavedProgress(progressData);
                        
                        // Automatically redirect to saved step after a brief delay
                        setTimeout(() => {
                            const route = getRouteForStep(progressData.currentStep);
                            navigate(`/client-onboarding/${token}/${route}`);
                        }, 1500);
                    }
                } else {
                    setError(response.message || 'This onboarding link is not valid.');
                }
            } catch (err) {
                console.error('Token validation error:', err);
                setError('Unable to validate your onboarding link. Please try again or contact support.');
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            validateToken();
        } else {
            setError('No onboarding token provided.');
            setLoading(false);
        }
    }, [token]);

    const handleProceed = () => {
        // If there's saved progress, resume from that step
        if (savedProgress && savedProgress.currentStep > 1) {
            const route = getRouteForStep(savedProgress.currentStep);
            navigate(`/client-onboarding/${token}/${route}`);
        } else {
            navigate(`/client-onboarding/${token}/terms`);
        }
    };

    const handleStartOver = () => {
        // Start from the beginning, ignoring saved progress
        navigate(`/client-onboarding/${token}/terms`);
    };

    const handleCancel = () => {
        // Could redirect to a general info page or just show a message
        navigate('/');
    };

    // Loading state
    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <p>Validating your onboarding link...</p>
                </div>
            </div>
        );
    }

    // Error state - Invalid or expired token
    if (error || !tokenValid) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.logoContainer}>
                        <img 
                            src="/Alt Logo Flat.png" 
                            alt="WETLAB" 
                            className={styles.logo}
                        />
                    </div>
                    <div className={styles.errorContent}>
                        <div className={styles.errorIcon}>⚠️</div>
                        <h2>Link Not Valid</h2>
                        <p className={styles.errorMessage}>{error}</p>
                        <p className={styles.helpText}>
                            If you need assistance, please contact our support team.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Welcome screen
    return (
        <>
            <OnboardingProgressBar />
            <div className={styles.container}>
                <div className={styles.card}>
                <div className={styles.logoContainer}>
                    <img 
                        src="/Alt Logo Flat.png" 
                        alt="WETLAB" 
                        className={styles.logo}
                    />
                </div>
                
                <div className={styles.welcomeContent}>
                    <h1 className={styles.welcomeTitle}>Welcome to WETLAB</h1>
                    <p className={styles.welcomeMessage}>
                        Thank you for choosing WETLAB for your analysis needs.
                    </p>
                    
                    {savedProgress && savedProgress.currentStep > 1 && (
                        <div className={styles.resumeNotice}>
                            <div className={styles.resumeIcon}>📋</div>
                            <div className={styles.resumeContent}>
                                <h3>Resuming Your Progress...</h3>
                                <p>
                                    We found your saved progress. Redirecting you to where you left off...
                                </p>
                                <div className={styles.spinner}></div>
                            </div>
                        </div>
                    )}
                    
                    <div className={styles.infoBox}>
                        <h2 className={styles.infoTitle}>Getting Started</h2>
                        <p className={styles.infoText}>
                            You're about to begin the onboarding process. This will help us gather
                            the necessary information to set up your WETLAB account and services.
                        </p>
                        
                        <div className={styles.featuresList}>
                            <div className={styles.feature}>
                                <span className={styles.featureIcon}>💾</span>
                                <span className={styles.featureText}>
                                    <strong>Save your progress:</strong> You can save and return to this form at any time
                                </span>
                            </div>
                            <div className={styles.feature}>
                                <span className={styles.featureIcon}>🔒</span>
                                <span className={styles.featureText}>
                                    <strong>Secure & private:</strong> Your information is always encrypted and protected
                                </span>
                            </div>
                            <div className={styles.feature}>
                                <span className={styles.featureIcon}>⏱️</span>
                                <span className={styles.featureText}>
                                    <strong>Takes about 10-15 minutes:</strong> Complete at your own pace
                                </span>
                            </div>
                        </div>
                    </div>

                    {tokenData?.expiresAt && (
                        <div className={styles.expiryNotice}>
                            <span className={styles.clockIcon}>🕒</span>
                            <span>This link expires {publicTokenService.formatExpirationTime(tokenData.expiresAt)}</span>
                        </div>
                    )}
                    
                    <div className={styles.actions}>
                        <button 
                            onClick={handleProceed}
                            className={styles.proceedButton}
                        >
                            {savedProgress && savedProgress.currentStep > 1 ? 'Continue Where I Left Off' : 'Begin Onboarding'}
                        </button>
                        {savedProgress && savedProgress.currentStep > 1 && (
                            <button 
                                onClick={handleStartOver}
                                className={styles.secondaryButton}
                            >
                                Start Over
                            </button>
                        )}
                        <button 
                            onClick={handleCancel}
                            className={styles.cancelButton}
                        >
                            Not Now
                        </button>
                    </div>
                    
                    <p className={styles.supportText}>
                        Need help? Contact us at <a href="mailto:support@wetlaboratory.com">support@wetlaboratory.com</a>
                    </p>
                </div>
            </div>
            </div>
        </>
    );
};

export default ClientOnboardingWelcome;
